import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    SmsTypeDTO,
    useDeleteSmsTypeApi,
    useSaveSmsTypeApi,
    useSearchSmsTypeApi,
} from '../../../api/sms-type-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveSmsTypeModal } from './save-sms-type-modal';

export function SmsTypeApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedSmsType, setSelectedSmsType] = useState<SmsTypeDTO | undefined>();
    const smsTypeInit = useSearchSmsTypeApi();
    const saveSmsType = useSaveSmsTypeApi();
    const deleteSmsType = useDeleteSmsTypeApi();

    const { result, error, busy, initialized, initializing } = smsTypeInit;

    const columns: IBasicTableColumn<SmsTypeDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 80,
            },
            {
                Header: <T>title</T>,
                name: 'title',
                width: 80,
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        smsTypeInit.post(null, {});
    }, [initialized, initializing, error, smsTypeInit]);

    const handleDelete = (smsType: SmsTypeDTO) => {
        bd.msgbox(
            translate('delete-sms-type'),
            translate('confirm-delete-sms-type'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteSmsType.remove(
                            smsType,
                            () => {
                                smsTypeInit.updateResult({
                                    ...result,
                                    smsTypes: result.smsTypes.filter(b => b.id !== smsType.id),
                                });
                                notify.dark(translate('selected-sms-type-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedSmsType(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (smsType: SmsTypeDTO) => {
        saveSmsType.post(
            null,
            smsType,
            res => {
                if (selectedSmsType) {
                    smsTypeInit.updateResult({
                        ...result,
                        smsTypes: result.smsTypes.map(b => (b.id === smsType.id ? res.smsType : b)),
                    });
                } else {
                    smsTypeInit.updateResult({
                        ...result,
                        smsTypes: [res.smsType, ...result.smsTypes],
                    });
                }

                setShowModal(false);
                setSelectedSmsType(undefined);
                notify.success(
                    selectedSmsType
                        ? translate('selected-sms-type-updated-successfully')
                        : translate('new-sms-type-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => smsTypeInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="sms-type"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-sms-type</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedSmsType(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.smsTypes}
                        onRowClick={({ row }) => {
                            setSelectedSmsType(row);
                            setShowModal(true);
                        }}
                        hover
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    <SaveSmsTypeModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedSmsType(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedSmsType={selectedSmsType}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
